import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import {
  firstValueFrom,
  merge,
  Observable,
  Subject,
  Subscription,
  switchMap,
  timer,
  of,
  concatMap,
  share
} from 'rxjs';

import { iocNames } from '../../iocNames';
import type { GqlService } from '../../services/GqlService';
import { GetCardsDataDocument } from './getCardsData.generated';
import { subscribeUniversal } from '../../utils/subscribeUniversal';
import { AnyUserCard, UserCard } from '../../models/UserCard';
import type { EntityCache } from '../../services/EntityCache';
import type { EventBus } from '../../services/EventBus';

const REFETCH_INTERVAL_MS = 10000;

const startTicking = () => timer(0, REFETCH_INTERVAL_MS);

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
iocNames;

export interface UserCardsDict {
  list: AnyUserCard[] | undefined;
  archivedList: AnyUserCard[] | undefined;
  getById(id: number): AnyUserCard | undefined;
  isLoading: boolean;
  error: unknown;
  refreshManually: () => Promise<void>;
}

export class UserCardsStore implements UserCardsDict {
  @observable
  private allList: AnyUserCard[] | undefined;

  @observable
  public isLoading = false;

  @observable
  public error: unknown;

  private manualRefreshSubject: Subject<number> = new Subject();

  private fetchingObservable: Observable<void>;

  constructor(
    private gqlService: GqlService,
    private entityCache: EntityCache,
    private eventBus: EventBus
  ) {
    makeObservable(this);

    this.fetchingObservable = merge(of(0), this.manualRefreshSubject).pipe(
      switchMap(startTicking),
      concatMap(this.refresh),
      share()
    );

    let fetchingSubscription: Subscription | undefined;
    subscribeUniversal(
      this,
      ['allList', 'isLoading', 'error'],
      () => {
        fetchingSubscription = this.fetchingObservable.subscribe();
      },
      () => {
        fetchingSubscription?.unsubscribe();
      }
    );

    this.eventBus.listen(
      e => e.name === 'UserCardCreatedEvent',
      () => {
        this.manualRefreshSubject.next(1);
      }
    );
  }

  @computed
  get list() {
    // @ts-ignore
    return this.allList?.filter(card => !card.isArchived);
  }

  @computed
  get archivedList() {
    // @ts-ignore
    return this.allList?.filter(card => card.isArchived);
  }

  getById(id: number): AnyUserCard | undefined {
    // позволяет срабатывать subscribeUniversal в случае если кто-то обратился за карточкой
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    this.list;
    return this.entityCache.getEntityById(UserCard.name, id);
  }

  public refreshManually = () => {
    if (!this.fetchingObservable) throw new Error('fetchingObservable is not defined');

    const result = firstValueFrom(this.fetchingObservable);
    this.manualRefreshSubject.next(1);
    return result;
  };

  @action
  private refresh = (): Promise<void> => {
    this.isLoading = true;
    this.error = undefined;

    return this.gqlService
      .makeRequest({
        requestOptions: {
          document: GetCardsDataDocument
        },
        needAuthorization: true
      })
      .then(result => {
        runInAction(() => {
          this.allList = this.entityCache.deserialize(
            UserCard,
            result.dizraptor.portfolio?.userCardsV2.concat(
              result.dizraptor.portfolio?.userArchivedCardsV2
            )
          ) as any as AnyUserCard[];
        });
      })
      .catch(err => {
        runInAction(() => {
          this.error = err;
        });
      })
      .finally(() => {
        runInAction(() => {
          this.isLoading = false;
        });
      });
  };
}
